import type { ScoreTimeline } from '@/types';
import { computeScrollLeft, type ScrollAnchor } from './notationCursor';

/**
 * OTO-26: context for inverting the notation cursor's time->scrollLeft mapping.
 * Field-for-field the same inputs `useNotationCursor` feeds `computeScrollLeft`.
 */
export interface ScrubMappingContext {
  measures: ScoreTimeline['measures'];
  totalDuration: number;
  measureXMap: Map<number, number>;
  beatAnchors?: ScrollAnchor[];
  contentScrollWidth: number;
  containerWidth: number;
}

/**
 * FX2: forward map from time to gesture-domain scroll (physical domain).
 * Uses pure content-x (cursorPosition: 0) to avoid the 25% offset + clamp
 * artifacts, yielding a monotonic map from 0 at t=0 to maxScrollLeft at t=end.
 * This is the basis for the new scrollLeftToTime inverse.
 */
export function timeToScrollLeft(time: number, ctx: ScrubMappingContext): number {
  if (!(ctx.totalDuration > 0)) return 0;
  const clampedTime = Math.max(0, Math.min(time, ctx.totalDuration));

  // Raw content-x at this time (no viewport offset, no clamp)
  const contentX = computeScrollLeft({
    currentTime: clampedTime,
    measures: ctx.measures,
    totalDuration: ctx.totalDuration,
    measureXMap: ctx.measureXMap,
    contentScrollWidth: ctx.contentScrollWidth,
    containerWidth: ctx.containerWidth,
    beatAnchors: ctx.beatAnchors,
    cursorPosition: 0, // FX2: extract pure x, no offset
  });

  // Compute the gesture-domain boundaries
  const startX = computeScrollLeft({
    currentTime: 0,
    measures: ctx.measures,
    totalDuration: ctx.totalDuration,
    measureXMap: ctx.measureXMap,
    contentScrollWidth: ctx.contentScrollWidth,
    containerWidth: ctx.containerWidth,
    beatAnchors: ctx.beatAnchors,
    cursorPosition: 0,
  });

  const endX = computeScrollLeft({
    currentTime: ctx.totalDuration,
    measures: ctx.measures,
    totalDuration: ctx.totalDuration,
    measureXMap: ctx.measureXMap,
    contentScrollWidth: ctx.contentScrollWidth,
    containerWidth: ctx.containerWidth,
    beatAnchors: ctx.beatAnchors,
    cursorPosition: 0,
  });

  const maxScrollLeft = Math.max(0, ctx.contentScrollWidth - ctx.containerWidth);
  const span = endX - startX;

  if (span <= 0) return 0; // content fits in viewport
  // FX2: rescale [startX, endX] → [0, maxScrollLeft]
  return ((contentX - startX) / span) * maxScrollLeft;
}

const COARSE_SAMPLES = 256;
const REFINE_ITERATIONS = 24;

function scrollLeftAt(time: number, ctx: ScrubMappingContext): number {
  return timeToScrollLeft(time, ctx);
}

function refine(loT: number, hiT: number, target: number, ctx: ScrubMappingContext): number {
  let lo = loT;
  let hi = hiT;
  const ascending = scrollLeftAt(hi, ctx) >= scrollLeftAt(lo, ctx);
  for (let i = 0; i < REFINE_ITERATIONS; i += 1) {
    const mid = (lo + hi) / 2;
    const x = scrollLeftAt(mid, ctx);
    if (x === target) return mid;
    if (x < target === ascending) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}

/**
 * FX2: inverse of timeToScrollLeft (physical scroll → time).
 * Numerically inverts the NEW gesture-domain forward mapping (coarse scan +
 * bisection refine), so scrub position and cursor position can never disagree.
 * Non-monotonic regions (repeats) yield multiple candidates; nearest to
 * referenceTime wins. Plateaus resolve to referenceTime clamped into plateau.
 * Deterministic: no randomness, fixed iteration counts.
 */
export function scrollLeftToTime(
  targetScrollLeft: number,
  ctx: ScrubMappingContext,
  referenceTime: number
): number {
  const { totalDuration } = ctx;
  if (!(totalDuration > 0)) return 0;

  const xs: number[] = new Array(COARSE_SAMPLES + 1);
  let lowX = Infinity;
  let highX = -Infinity;
  for (let i = 0; i <= COARSE_SAMPLES; i += 1) {
    const x = scrollLeftAt((i / COARSE_SAMPLES) * totalDuration, ctx);
    xs[i] = x;
    if (x < lowX) lowX = x;
    if (x > highX) highX = x;
  }

  // FX2: clamp target to the gesture domain [lowX, highX]
  // (in the new convention, lowX ≈ 0, highX ≈ maxScrollLeft by design)
  const target = Math.min(highX, Math.max(lowX, targetScrollLeft));

  const candidates: number[] = [];
  for (let i = 1; i <= COARSE_SAMPLES; i += 1) {
    const prevX = xs[i - 1];
    const x = xs[i];
    if ((prevX - target) * (x - target) <= 0) {
      const t0 = ((i - 1) / COARSE_SAMPLES) * totalDuration;
      const t1 = (i / COARSE_SAMPLES) * totalDuration;
      candidates.push(
        prevX === x ? Math.min(Math.max(referenceTime, t0), t1) : refine(t0, t1, target, ctx)
      );
    }
  }

  if (candidates.length === 0) {
    const span = highX - lowX;
    const fallback = span > 0 ? ((target - lowX) / span) * totalDuration : referenceTime;
    return Math.min(totalDuration, Math.max(0, fallback));
  }

  let best = candidates[0];
  let bestDist = Math.abs(best - referenceTime);
  for (const c of candidates) {
    const d = Math.abs(c - referenceTime);
    if (d < bestDist - 1e-9) {
      best = c;
      bestDist = d;
    }
  }
  return Math.min(totalDuration, Math.max(0, best));
}
